import React, { useCallback, useEffect, useState } from 'react';
import { ChevronRight, Folder, Loader2 } from 'lucide-react';
import { listGoogleDriveFolders } from '../../../api/googleDrive';
import { googleDriveFolderUrl } from '../constants';

const ROOT_CRUMB = { id: 'root', name: 'Meu Drive' };

function GoogleDriveFolderPicker({ connected, value, onChange }) {
  const [breadcrumbs, setBreadcrumbs] = useState([ROOT_CRUMB]);
  const [folders, setFolders] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const currentParent = breadcrumbs[breadcrumbs.length - 1];

  const loadFolders = useCallback(
    async ({ parentId, pageToken, append = false } = {}) => {
      const isLoadMore = Boolean(pageToken);
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError('');

      try {
        const data = await listGoogleDriveFolders({
          parentId: parentId ?? currentParent.id,
          pageToken,
        });
        setFolders((current) =>
          append ? [...current, ...(data.folders || [])] : data.folders || [],
        );
        setNextPageToken(data.next_page_token || null);
      } catch (err) {
        const message =
          err.response?.data?.detail ||
          err.message ||
          'Não foi possível carregar as pastas.';
        setError(message);
        if (!append) {
          setFolders([]);
          setNextPageToken(null);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [currentParent.id],
  );

  useEffect(() => {
    if (!connected) {
      setFolders([]);
      setBreadcrumbs([ROOT_CRUMB]);
      setNextPageToken(null);
      setError('');
      return;
    }
    loadFolders({ parentId: currentParent.id });
  }, [connected, currentParent.id, loadFolders]);

  const handleEnterFolder = (folder) => {
    setBreadcrumbs((current) => [...current, { id: folder.id, name: folder.name }]);
  };

  const handleBreadcrumbClick = (index) => {
    setBreadcrumbs((current) => current.slice(0, index + 1));
  };

  const handleSelectFolder = (folder) => {
    onChange?.({
      folderId: folder.id,
      folderName: folder.name,
      folderUrl: googleDriveFolderUrl(folder.id),
    });
  };

  const handleSelectCurrent = () => {
    if (currentParent.id === 'root') {
      return;
    }
    onChange?.({
      folderId: currentParent.id,
      folderName: currentParent.name,
      folderUrl: googleDriveFolderUrl(currentParent.id),
    });
  };

  if (!connected) {
    return null;
  }

  const selectedId = value?.folderId || null;

  return (
    <div className="rounded-lg border border-surface-border bg-white overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-surface-border bg-slate-50 text-xs">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb.id}>
            {index > 0 && (
              <ChevronRight className="h-3.5 w-3.5 text-content-muted shrink-0" aria-hidden />
            )}
            <button
              type="button"
              onClick={() => handleBreadcrumbClick(index)}
              className={`truncate max-w-[140px] sm:max-w-none rounded px-1 py-0.5 hover:bg-slate-200/80 ${
                index === breadcrumbs.length - 1
                  ? 'font-semibold text-content-primary'
                  : 'text-forge-600 hover:text-forge-700'
              }`}
            >
              {crumb.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {currentParent.id !== 'root' && (
        <div className="px-3 py-2 border-b border-surface-border bg-forge-50/50">
          <button
            type="button"
            onClick={handleSelectCurrent}
            className="text-xs font-medium text-forge-700 hover:text-forge-800 underline"
          >
            Selecionar esta pasta ({currentParent.name})
          </button>
        </div>
      )}

      <div className="max-h-48 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-content-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Carregando pastas...
          </div>
        )}

        {!loading && error && (
          <div className="px-3 py-4 text-center space-y-2">
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
            <button
              type="button"
              onClick={() => loadFolders({ parentId: currentParent.id })}
              className="text-xs text-forge-600 hover:text-forge-700 underline"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {!loading && !error && folders.length === 0 && (
          <p className="px-3 py-6 text-sm text-center text-content-muted">
            Nenhuma subpasta aqui.
            {currentParent.id !== 'root' && (
              <>
                {' '}
                Use &quot;Selecionar esta pasta&quot; acima se esta for a pasta desejada.
              </>
            )}
          </p>
        )}

        {!loading && !error && folders.length > 0 && (
          <ul className="divide-y divide-surface-border">
            {folders.map((folder) => {
              const isSelected = selectedId === folder.id;
              return (
                <li key={folder.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
                  <button
                    type="button"
                    onClick={() => handleEnterFolder(folder)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                  >
                    <Folder
                      className={`h-4 w-4 shrink-0 ${
                        isSelected ? 'text-forge-600' : 'text-amber-500'
                      }`}
                      aria-hidden
                    />
                    <span
                      className={`text-sm truncate ${
                        isSelected ? 'font-medium text-forge-800' : 'text-content-primary'
                      }`}
                    >
                      {folder.name}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelectFolder(folder)}
                    className="shrink-0 text-xs font-medium text-forge-600 hover:text-forge-700 px-2 py-1 rounded border border-forge-200 hover:bg-forge-50"
                  >
                    Selecionar
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {!loading && !error && nextPageToken && (
        <div className="border-t border-surface-border px-3 py-2">
          <button
            type="button"
            disabled={loadingMore}
            onClick={() =>
              loadFolders({
                parentId: currentParent.id,
                pageToken: nextPageToken,
                append: true,
              })
            }
            className="w-full text-xs font-medium text-forge-600 hover:text-forge-700 disabled:opacity-50"
          >
            {loadingMore ? 'Carregando...' : 'Carregar mais pastas'}
          </button>
        </div>
      )}

      {selectedId && value?.folderName && (
        <div className="border-t border-surface-border px-3 py-1.5 bg-emerald-50/80">
          <p className="text-xs text-emerald-800 truncate">
            <span className="font-medium">{value.folderName}</span>
          </p>
        </div>
      )}
    </div>
  );
}

export default GoogleDriveFolderPicker;
